import CardCommon from '@op/components/CardCommon';
import { SymbolType, barIcon, lineIcon, pieIcon } from '@op/constants/chart';
import { gameInfoType } from '@op/types/common';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'tea-component';
import BarLineChart from '../../components/BarLineChart';
import PieChart from '../../components/PieChart';
import { getKolBrandPerformance } from '@op/services/influencer';
import { translateText } from '../../utils/text';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { initialUpperCase } from '@op/utils';
import useLanguage from '@op/hooks/useLanguage';
import {
  commonMetricsOptions,
  metricsOptions,
  streamMetricsOptionsMap,
} from '../../constants';
import './index.scss';
import { NO_DATE } from '@op/constants';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

export default function PublicationPerformance({
  filter,
  gameInfo,
  mediaType,
}: {
  filter: any;
  gameInfo: gameInfoType;
  mediaType: string;
}) {
  const [filterType, setFilterType] = useState<'views' | 'engagement' | string>(
    'views',
  );
  const [publicationType, setPublicationType] = useState<SymbolType>('pie');
  const [performanceType, setPerformanceType] = useState<SymbolType>('line');

  const [filters, setFilters] = useState(commonMetricsOptions);
  useEffect(() => {
    if (mediaType === 'stream') {
      setFilters(metricsOptions);
      setFilterType(metricsOptions[0].value);
    } else {
      setFilters(commonMetricsOptions);
      setFilterType(commonMetricsOptions[0].value);
    }

    setPublicationType(mediaType === 'sum' ? 'pie' : 'bar');
    setPerformanceType('line');
  }, [mediaType]);

  const [postPie, setPostPie] = useState([]);
  const [postTimeline, setPostTimeline] = useState<{
    timeline_of_start_time: string;
    timeline_of_end_time: string;
    timeline: Array<any>;
  }>({
    timeline_of_start_time: '',
    timeline_of_end_time: '',
    timeline: [],
  });

  const [otherPieMap, setOtherPie] = useState<{ [key: string]: [] }>({});
  const [otherTimeline, setOtherTimeline] = useState<{
    [key: string]: {
      timeline_of_start_time: string;
      timeline_of_end_time: string;
      timeline: Array<any>;
    };
  }>({});

  const [loading, setLoading] = useState(false);
  const { downloadLegacy } = useDownloadParams();
  const [postsDownloading, setPostsDownloading] = useState(false);
  const [performanceDownloading, setPerformanceDownloading] =
    useState(false);

  useEffect(() => {
    setLoading(true);
    setOtherPie({});
    setPostPie([]);
    setPostTimeline({
      timeline_of_start_time: '',
      timeline_of_end_time: '',
      timeline: [],
    });
    setOtherTimeline({});
    getKolBrandPerformance({
      ...filter,
      metrics:
        mediaType === 'stream'
          ? ['posts', 'hoursWatched', 'airtime', 'peakCCV', 'avgCCV']
          : ['posts', 'views', 'engagement'],
    })
      .then((data: any) => {
        const { metrics, timeline_of_start_time, timeline_of_end_time } = data;

        const { posts, ...otherMetrics } = metrics;

        const pieMap: any = {};
        const timelineMap: any = {};
        for (const key in otherMetrics) {
          const { list, timeline } = otherMetrics[key];
          pieMap[key] = list;
          timelineMap[key] = {
            timeline,
            timeline_of_start_time,
            timeline_of_end_time,
          };
        }

        setOtherPie(pieMap);
        setOtherTimeline(timelineMap);

        setPostPie(posts.list || []);
        setPostTimeline({
          timeline: posts.timeline,
          timeline_of_start_time,
          timeline_of_end_time,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter]);

  const { pie, timeline } = useMemo(() => {
    return {
      pie: otherPieMap[filterType] || [],
      timeline: otherTimeline[filterType] || {
        timeline_of_start_time: '',
        timeline_of_end_time: '',
        timeline: [],
      },
    };
  }, [otherPieMap, otherTimeline, filterType]);

  const { formatMessage, formatOptionsMessage } = useLanguage();

  const translate = (text: string, type: any = filterType) =>
    translateText(text, type, (text: string, oriText?: string) => {
      let newText = text;
      if (mediaType === 'stream') {
        if (type === 'posts') {
          newText = 'Streaming';
        } else {
          newText = streamMetricsOptionsMap[oriText!];
        }
      }
      return formatMessage({ id: newText || NO_DATE.EMPTY });
    });

  const download = async (
    type: string = filterType,
    sheetName = 'Performance Analysis',
  ) => {
    buriedBtnPoint('IBT_Download', sheetName + '-' + type);
    const isPosts = sheetName === 'Publication Counts';
    if (isPosts) {
      setPostsDownloading(true);
    } else {
      setPerformanceDownloading(true);
    }
    try {
      await downloadLegacy({
        limit: 150000,
        params: {
          ...filter,
          metrics: [type],
          INNER_METRIC_TYPE: type,
        },
        downloadFunc: 'getKolBrandPerformance',
        sheetName: sheetName,
        simpleName: sheetName,
      });
    } finally {
      if (isPosts) {
        setPostsDownloading(false);
      } else {
        setPerformanceDownloading(false);
      }
    }
  };

  return (
    <Row className="publication-performance-wrapper">
      <Col>
        <CardCommon
          title={formatMessage({ id: 'Publication Counts' })}
          operations={
            mediaType === 'sum'
              ? [barIcon, lineIcon, pieIcon]
              : [barIcon, lineIcon]
          }
          hasDownload
          downloadLoading={postsDownloading}
          operationInfo={[publicationType, setPublicationType]}
          loading={loading}
          loadingHeight={330}
          className="dbh-studio-publication-posts-card"
          handleOperationsOnClick={(item: any) =>
            setPublicationType(item.symbol)
          }
          handleDownloadOnClick={() => download('posts', 'Publication Counts')}
        >
          {publicationType === 'pie' ? (
            <PieChart
              title={formatMessage({ id: 'Publication Counts' })}
              filter={filter}
              data={postPie}
              translate={(text: string) => translate(text, 'posts')}
            ></PieChart>
          ) : (
            <BarLineChart
              type={publicationType}
              data={postTimeline}
              dateType={filter.date_type}
              title={formatMessage({ id: 'Publication Counts' })}
              translate={(text: string) => translate(text, 'posts')}
            ></BarLineChart>
          )}
        </CardCommon>
      </Col>
      <Col>
        <CardCommon
          title={formatMessage({ id: 'Performance Analysis' })}
          hasDownload
          downloadLoading={performanceDownloading}
          operations={
            mediaType === 'stream'
              ? [barIcon, lineIcon]
              : [barIcon, lineIcon, pieIcon]
          }
          handleDownloadOnClick={download}
          loading={loading}
          loadingHeight={290}
          className="dbh-studio-publication-performance-card"
          operationInfo={[performanceType, setPerformanceType]}
          handleOperationsOnClick={(item: any) =>
            setPerformanceType(item.symbol)
          }
          filterInfo={[filterType, setFilterType]}
          handleFilterOnChange={(value: any) => setFilterType(value)}
          filters={formatOptionsMessage(filters)}
        >
          {performanceType === 'pie' ? (
            <PieChart
              title={formatMessage({ id: initialUpperCase(filterType) })}
              filter={filter}
              height={290}
              data={pie}
              translate={translate}
            ></PieChart>
          ) : (
            <BarLineChart
              type={performanceType}
              data={timeline}
              translate={translate}
              dateType={filter.date_type}
              height={290}
              noTotal={true}
              title=""
            ></BarLineChart>
          )}
        </CardCommon>
      </Col>
    </Row>
  );
}
